import { ErrorResponse } from '@fewangsit/workspace-api-services/src/types/projectQcService.type';
import { AxiosError } from 'axios';
import { uniqueId } from 'lodash-es';
import { useToast as usePrimevueToast } from 'primevue/usetoast';

import { WangsIcons } from 'lib/components/icon/Icon.vue.d';
import { ToastMessageOptions } from 'lib/components/toast/Toast.vue.d';
import eventBus from 'lib/event-bus';
import { useWangsVueConfig } from 'lib/plugins/WangsVue';

export interface ToastMethod {
  /**
   * Show toast with message options.
   * @param options
   */
  add(options: ToastMessageOptions): void;

  /**
   * Clears the messages that belongs to the group.
   * @param {string} group - Name of the message group.
   */
  removeGroup(group: string): void;

  removeAllGroups(): void;
}

/**
 * Configuration options for the `useToast` hook.
 */
export interface UseToastConfig {
  /**
   * Icons for each severity level.
   * Keys represent the severity, and values are the corresponding icon identifiers.
   *
   * @example
   * {
   *   success: 'emotion-happy-fill',
   *   error: 'emotion-unhappy-fill',
   * }
   */
  icons?: Record<string, WangsIcons>;
}

/**
 * Hook to create and manage toast notifications.
 *
 * @param {UseToastConfig} config - Configuration object for the toast notifications.
 * Refer to {@link UseToastConfig} for the structure and details of this object.
 *
 * @returns {ToastMethod} A method to trigger toast notifications.
 * The method accepts {@link ToastMessageOptions} to customize individual notifications.
 *
 * @example
 * const toast = useToast({
 *   icons: {
 *     success: 'emotion-happy-fill',
 *     error: 'emotion-unhappy-fill',
 *   }
 * });
 *
 * // Overiding the default config with ToastMessageOptions
 * toast.add({
 *   message: 'Operation completed successfully!',
 *   customMessage: true,
 *   icon: 'custom-success-icon',
 * });
 */
const useToast = ({ icons }: UseToastConfig = {}): ToastMethod => {
  const { locale } = useWangsVueConfig('Toast');
  const toast = usePrimevueToast();
  return {
    add: (options: ToastMessageOptions): void => {
      const {
        message,
        customMessage,
        error,
        severity,
        life,
        group = 'default-toast',
        ...rest
      } = options;

      let networkErrorMessage = '';

      if (error instanceof Error) {
        if (error && (error as AxiosError<ErrorResponse>).isAxiosError) {
          const isNetworkError =
            (error as AxiosError<ErrorResponse>).code === 'ERR_NETWORK';

          networkErrorMessage = isNetworkError
            ? (locale.networkErrorMessage ?? '')
            : '';
        }
      }

      const formattedSeverity = error ? 'error' : (severity ?? 'success');

      let messageTemplate = '{message}';
      if (formattedSeverity === 'success')
        messageTemplate = locale.successMessage ?? messageTemplate;
      else if (formattedSeverity === 'error')
        messageTemplate = locale.errorMessage ?? messageTemplate;

      const detail = `${messageTemplate}${networkErrorMessage}`.replace(
        '{message}',
        message,
      );

      const icon = rest.icon ?? icons?.[formattedSeverity];

      toast.add({
        severity: formattedSeverity,
        detail: customMessage ? message : detail,
        life: life ?? 3000,
        group,
      });

      eventBus.emit('toast:add', {
        messageId: uniqueId(),
        ...options,
        severity: formattedSeverity,
        group,
        icon,
      });
    },

    removeGroup: (group: string): void => {
      toast.removeGroup(group);
    },

    removeAllGroups: (): void => {
      toast.removeAllGroups();
    },
  };
};

export default useToast;
